import {useState} from 'react';
import {Link} from 'react-router';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LinkStyle} from '@ui/buttons/external-link';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {StaticPageTitle} from '../../seo/static-page-title';
import {TwoFactorChallengePage} from './two-factor/two-factor-challenge-page';
import {LoginPayload, useLogin} from '../requests/use-login';

const ADMIN_REDIRECT_URI = '/admin';

export function AdminLoginPage() {
  const [isTwoFactor, setIsTwoFactor] = useState(false);

  if (isTwoFactor) {
    return <TwoFactorChallengePage redirectTo={ADMIN_REDIRECT_URI} />;
  }

  return <AdminLoginForm onTwoFactorChallenge={() => setIsTwoFactor(true)} />;
}

interface AdminLoginFormProps {
  onTwoFactorChallenge: () => void;
}
function AdminLoginForm({onTwoFactorChallenge}: AdminLoginFormProps) {
  const form = useForm<LoginPayload>({defaultValues: {remember: false}});
  const login = useLogin(form, ADMIN_REDIRECT_URI);

  return (
    <AuthLayout heading={<Trans message="Admin sign in" />}>
      <StaticPageTitle>
        <Trans message="Admin Login" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={payload => {
          login.mutate(payload, {
            onSuccess: response => {
              if (response.two_factor) {
                onTwoFactorChallenge();
              }
            },
          });
        }}
      >
        <FormTextField
          className="mb-24"
          name="email"
          type="email"
          autoFocus
          autoComplete="username"
          label={<Trans message="Email" />}
          required
        />
        <FormTextField
          className="mb-16"
          name="password"
          type="password"
          autoComplete="current-password"
          label={<Trans message="Password" />}
          required
        />
        <div className="mb-24 flex items-center justify-between text-sm">
          <FormCheckbox name="remember">
            <Trans message="Remember me" />
          </FormCheckbox>
          <Link className={LinkStyle} to="/forgot-password">
            <Trans message="Forgot password?" />
          </Link>
        </div>
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={login.isPending}
        >
          <Trans message="Sign in" />
        </Button>
      </Form>
    </AuthLayout>
  );
}
